import { Link } from 'react-router'
import { Section, Seo } from '@/components/primitives'
import {
  WEBSITE_PRIVACY_NOTICE,
  WEBSITE_TERMS_OF_USE,
  type LegalBlock,
  type LegalSegment,
} from '@/pages/legalContent'

function LegalText({ content }: { content: LegalSegment[] }) {
  return content.map((segment, index) => {
    if (typeof segment === 'string') return <span key={index}>{segment}</span>

    const className =
      'font-semibold text-signal underline decoration-signal/40 underline-offset-4 transition-colors duration-micro hover:text-signal-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal focus-visible:ring-offset-2 focus-visible:ring-offset-base-850'

    return segment.href.startsWith('/') ? (
      <Link key={`${segment.href}-${index}`} to={segment.href} className={className}>
        {segment.text}
      </Link>
    ) : (
      <a key={`${segment.href}-${index}`} href={segment.href} className={className}>
        {segment.text}
      </a>
    )
  })
}

function LegalBlockView({ block }: { block: LegalBlock }) {
  if (block.kind === 'paragraph') {
    return (
      <p className="max-w-[72ch] text-[15px] leading-7 text-ink-mute md:text-base md:leading-8">
        <LegalText content={block.content} />
      </p>
    )
  }

  if (block.kind === 'subheading') {
    return (
      <h3 className="pt-3 text-lg font-extrabold tracking-tight text-ink md:text-xl">
        {block.title}
      </h3>
    )
  }

  if (block.kind === 'list') {
    return (
      <ul className="max-w-[72ch] space-y-3 text-[15px] leading-7 text-ink-mute md:text-base md:leading-8">
        {block.items.map((listItem, index) => (
          <li key={index} className="flex gap-3">
            <span aria-hidden="true" className="mt-[0.72rem] h-1.5 w-1.5 shrink-0 bg-signal" />
            <span>
              <LegalText content={listItem} />
            </span>
          </li>
        ))}
      </ul>
    )
  }

  if (block.kind === 'facts') {
    return (
      <dl className="max-w-[72ch] border-y border-base-line">
        {block.items.map((fact) => (
          <div key={fact.term} className="grid gap-3 border-t border-base-line py-6 first:border-t-0 md:grid-cols-[13rem_1fr] md:gap-8">
            <dt className="text-sm font-extrabold leading-6 text-ink">{fact.term}</dt>
            <dd className="space-y-2 text-sm leading-6 text-ink-mute">
              {fact.details.map((detail, index) => (
                <p key={index}>
                  <LegalText content={detail} />
                </p>
              ))}
            </dd>
          </div>
        ))}
      </dl>
    )
  }

  return (
    <address className="max-w-[72ch] border-y border-base-line py-5 not-italic text-[15px] leading-7 text-ink-mute md:text-base md:leading-8">
      {block.lines.map((line, index) => (
        <p key={index}>
          <LegalText content={line} />
        </p>
      ))}
    </address>
  )
}

export default function Legal({ kind }: { kind: 'privacy' | 'terms' }) {
  const document = kind === 'privacy' ? WEBSITE_PRIVACY_NOTICE : WEBSITE_TERMS_OF_USE

  return (
    <>
      <Seo
        title={`${document.title} | Esotech`}
        description={document.seoDescription}
      />

      <section aria-labelledby="legal-title" className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="field-grid pointer-events-none absolute inset-0 opacity-50 [mask-image:radial-gradient(70%_60%_at_50%_20%,black,transparent)]"
        />
        <div className="relative mx-auto w-full max-w-7xl px-5 py-20 sm:px-8 md:py-28">
          <div>
            <p className="eyebrow-mute">Legal</p>
            <h1
              id="legal-title"
              className="mt-5 max-w-4xl text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl"
            >
              {document.title}
            </h1>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-ink-mute md:text-lg">
              <LegalText content={document.intro} />
            </p>
            <p className="mt-6 max-w-3xl font-mono text-[10px] uppercase leading-5 tracking-[0.14em] text-ink-dim sm:text-[11px]">
              Effective date: {document.effectiveDate}
              <span aria-hidden="true" className="mx-2 text-base-linebright">|</span>
              Last updated: {document.reviewDate}
            </p>
          </div>
        </div>
      </section>

      <Section labelledBy="legal-body" className="bg-base-900/40">
        <h2 id="legal-body" className="sr-only">
          {document.title} details
        </h2>
        <div className="grid items-start gap-12 text-left lg:grid-cols-[15rem_minmax(0,48rem)] lg:gap-16">
          <nav
            aria-label={`${document.shortTitle} sections`}
            className="border-y border-base-line py-5 lg:sticky lg:top-24 lg:max-h-[calc(100svh-8rem)] lg:overflow-y-auto"
          >
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-dim">
              On this page
            </p>
            <ul className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-1">
              {document.sections.map((legalSection) => (
                <li key={legalSection.id}>
                  <a
                    href={`#${legalSection.id}`}
                    className="text-sm leading-5 text-ink-mute transition-colors duration-micro hover:text-signal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal focus-visible:ring-offset-2 focus-visible:ring-offset-base-900"
                  >
                    {legalSection.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <article>
            <aside
              aria-label="Key notice"
              className="rounded-sm border border-signal/30 bg-base-850 px-5 py-5 md:px-6"
            >
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-signal">
                Key notice
              </p>
              <p className="mt-3 max-w-[68ch] text-sm leading-7 text-ink-mute md:text-[15px]">
                <LegalText content={document.scopeNote} />
              </p>
            </aside>

            <div className="mt-10">
              {document.sections.map((legalSection) => (
                <section
                  key={legalSection.id}
                  id={legalSection.id}
                  aria-labelledby={`${legalSection.id}-title`}
                  className="scroll-mt-28 border-t border-base-line py-10 first:border-t-0 first:pt-0"
                >
                  <h2
                    id={`${legalSection.id}-title`}
                    className="max-w-[24ch] text-balance text-2xl font-extrabold leading-tight tracking-tight text-ink md:text-3xl"
                  >
                    {legalSection.title}
                  </h2>
                  <div className="mt-6 space-y-5">
                    {legalSection.blocks.map((block, index) => (
                      <LegalBlockView key={index} block={block} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </article>
        </div>
      </Section>
    </>
  )
}
